import axios from 'axios';
import config from 'config';
import { setupRequestInterceptors } from './interceptors';

export const PROVIDER_FILE_TYPES = {
  STATE_LICENSE: { title: 'State license', code: 'LICENSE', required: true },
  HCC: { title: 'HCC', code: 'HCC', required: false },
  W9: { title: 'W-9', code: 'W9', required: true },
  RATE_SHEET: { title: 'Rate sheet', code: 'RATES', required: true },
  QRIS: { title: 'QRIS', code: 'QRIS', required: true },
  INSURANCE: { title: 'INSURANCE', code: 'INSURANCE', required: true },
  DIPLOMA: { title: 'DIPLOMA', code: 'DIPLOMA', required: true },
  CERTIFICATION: { title: 'CPR/first aid certification', code: 'CERTIFICATION', required: true },
  // optional files
  EIN: { title: 'EIN', code: 'EIN' },
  NATIONAL_ACCREDITATION: { title: 'National accreditation', code: 'ACCREDITATION' },
  DIRECT_DEPOSIT: { title: 'Direct deposit', code: 'DIRECT_DEPOSIT' },
  EDUCATION: { title: 'Continuing education', code: 'EDUCATION' },
  OTHER: { title: 'Other documents', code: 'OTHER' },
  // System files
  APPLICATION_SUMMARY: { title: 'Application Summary', code: 'APPLICATION', required: false },
  DISCOUNT_VERIFICATION: { title: 'Rate Verification', code: 'RATE_VERIFICATION', required: false },
};
export const getProviderFileTypeFromCode = (code) => {
  const providerFileTypes = Object.values(PROVIDER_FILE_TYPES);
  return providerFileTypes.find((item) => item.code === code);
};
export const getProviderFileTypeTitleFromCode = (code) => {
  const providerFileTypes = Object.values(PROVIDER_FILE_TYPES);
  const providerFileType = providerFileTypes.find((item) => item.code === code);
  return providerFileType?.title;
};

const instance = axios.create({
  baseURL: `${config.API_BASE_URL}/providers`,
});
const request = setupRequestInterceptors(instance);

const api = {
  getCenterById: async (id, options) => {
    const { data } = await request.get(`/care-facilities/${id}`, options);
    return data;
  },
  getCareFacilities: async (orgId) => {
    const { data } = await request.get(`/organizations/${orgId}/care-facilities?size=20`);
    return data;
  },
  deleteCareFacility: async (id) => {
    const { data } = await request.delete(`/care-facilities/${id}`);
    return data;
  },
  getDiscountTypes: async () => {
    const { data } = await request.get('/options/12');
    return data;
  },
  updateCenter: async (params, id) => {
    const { data } = await request[id ? 'put' : 'post'](`/care-facilities${id ? `/${id}` : ''}`, params);
    return data;
  },
  submitApplication: async (appId) => {
    const { data } = await request.put(`/applications/${appId}/submit`);
    return data;
  },
  getPublicStatusList: async (statusType) => {
    const { data } = await request.get(`/options/3`);
    return data;
  },
  getApplicationStatus: async (id) => {
    const { data } = await request.get(`/application-statuses/${id}`);
    return data;
  },
  updateApplicationStatus: async (applicationid, statusId) => {
    const { data } = await request.put(`/application-statuses/${applicationid}/status?statusId=${statusId}`);
    return data;
  },
  assignCoordinator: async (params) => {
    const { data } = await request.post(`/applications/assign`, params);
    return data;
  },
  unAssignCoordinator: async (params) => {
    const { data } = await request.post(`/applications/unassign`, params);
    return data;
  },
  assignReviewer: async (params) => {
    const { data } = await request.post(`/applications/reviewer/assign`, params);
    return data;
  },
  getStaff: async (id) => {
    const { data } = await request.get(`/care-facilities/${id}/staff`);
    return data;
  },
  deleteStaff: async (id, staffId) => {
    const { data } = await request.delete(`/care-facilities/${id}/staff/${staffId}`);
    return data;
  },
  getOrganization: async (id) => {
    if (id === undefined || id === null) {
      const { data } = await request.get('/organizations');
      return data;
    }

    const { data } = await request.get(`/organizations/${id}`);
    return data;
  },
  addOrganization: async (dto) => {
    const { data } = await request.post('/organizations', dto);
    return data;
  },
  getOrganizationValidity: async (orgId) => {
    const { data } = await request.get(`/organizations/${orgId}/profile/validate`);
    return data;
  },
  updateOrganization: async (id, params) => {
    const { data } = await request[id ? 'put' : 'post'](`/organizations/${id ? id : ''}`, params);
    return data;
  },
  batchUpdateOrganization: async (orgId, params, careFacilityIds = []) => {
    const queryParams = careFacilityIds.length > 0 ? `?careFacilityIds=${careFacilityIds.join(',')}` : '';
    const { data } = await request.put(`/organizations/${orgId}/batch${queryParams}`, params);
    return data;
  },
  getApplicationNotes: async (id, params) => {
    const { data } = await request.get(`/applications/${id}/notes`, { params });
    return data;
  },
  saveNote: async (applicationId, params) => {
    const { data } = await request[params.id ? 'put' : 'post'](
      `/applications/${applicationId}/notes/${params.id ? params.id : ''}`,
      params,
    );
    return data;
  },
  getProviders: async (params) => {
    const response = await request.get('/search', { params });
    const { data } = response;
    return data;
  },
  acceptAgreement: async (appId, payload) => {
    const { data } = await request.post(`/applications/${appId}/agreement/accept`, payload);
    return data;
  },
  getApplicationAgreements: async (appId, options) => {
    const { data } = await request.get(`/applications/${appId}/agreements`, options);
    return data;
  },
  /*
   * FILES
   */
  getCareFacilityFiles: async (id) => {
    const { data } = await request.get(`/care-facilities/${id}/files`);
    return data;
  },
  uploadCareFacilityFile: async (id, file, fileType) => {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType || PROVIDER_FILE_TYPES.OTHER.code);

    const { data } = await request.post(`/care-facilities/${id}/files`, formData);
    return data;
  },
  deleteCareFacilityFile: async (id, fileId) => {
    const { data } = await request.delete(`/care-facilities/${id}/files/${fileId}`);
    return data;
  },
  /*
   * ORG FILES
   */
  getOrgFiles: async (orgId) => {
    const { data } = await request.get(`/organizations/${orgId}/files`);
    return data;
  },
  getOrgFileContents: async (orgId, fileId) => {
    const { data } = await request.get(`/organizations/${orgId}/files/${fileId}/contents`);
    return data;
  },
  uploadOrgFile: async (orgId, file, fileType) => {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType || PROVIDER_FILE_TYPES.OTHER.code);

    const { data } = await request.post(`/organizations/${orgId}/files`, formData);
    return data;
  },
  deleteOrgFile: async (orgId, fileId) => {
    const { data } = await request.delete(`/organizations/${orgId}/files/${fileId}`);
    return data;
  },
  /*
   * APPLICATIONS
   */
  createApplication: async (careFacilityId, payload) => {
    const { data } = await request.post(`/care-facilities/${careFacilityId}/applications`, payload);
    return data;
  },
  updateApplication: async (appId, payload) => {
    const { data } = await request.put(`/applications/${appId}`, payload);
    return data;
  },
  getApplication: async (appId) => {
    const { data } = await request.get(`/applications/${appId}`);
    return data;
  },
  getCarefacilityApplications: async (careFacilityId) => {
    const { data } = await request.get(`/care-facilities/${careFacilityId}/applications`);
    return data;
  },
  /*
   * Profile validity checks
   */
  getCareFacilityProfileValidity: async (careFacilityId) => {
    const { data } = await request.get(`/care-facilities/${careFacilityId}/profile/validate`);
    return data;
  },
  /*
   * Care facility files
   */
  getCareFacilityFileContents: async (careFacilityId, fileId) => {
    const { data } = await request.get(`/care-facilities/${careFacilityId}/files/${fileId}/contents`);
    return data;
  },
  /*
   * APPLICATION FILES
   */
  getApplicationFiles: async (appId) => {
    const { data } = await request.get(`/applications/${appId}/files`);
    return data;
  },
  getApplicationFileContents: async (appId, fileId) => {
    const { data } = await request.get(`/applications/${appId}/files/${fileId}/contents`);
    return data;
  },
  uploadApplicationFile: async (appId, file, fileType) => {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType || PROVIDER_FILE_TYPES.OTHER.code);

    const { data } = await request.post(`/applications/${appId}/files`, formData);
    return data;
  },
  deleteApplicationFile: async (appId, fileId) => {
    const { data } = await request.delete(`/applications/${appId}/files/${fileId}`);
    return data;
  },
  downloadAllApplicationFiles: async (appId) => {
    const { data } = await request.post(
      `/applications/${appId}/files/download`,
      {},
      {
        responseType: 'blob',
      },
    );
    return data;
  },
  /*
   * APPLICATION FEES
   */
  getFeeRates: async (id) => {
    const { data } = await request.get(`/applications/${id}/fees`);
    return data;
  },
  addUpdateFeeRate: async (applicationId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/applications/${applicationId}/fees${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },
  deleteFeeRate: async (applicationId, id) => {
    const { data } = await request.delete(`/applications/${applicationId}/fees/${id}`);
    return data;
  },

  /*
   * APPLICATION DISCOUNTS
   */
  getApplicationDiscounts: async (id) => {
    const { data } = await request.get(`/applications/${id}/discounts`);
    return data;
  },
  addUpdateApplicationDiscounts: async (applicationId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/applications/${applicationId}/discounts${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },
  deleteApplicationDiscounts: async (applicationId, id) => {
    const { data } = await request.delete(`/applications/${applicationId}/discounts/${id}`);
    return data;
  },

  /*
   * APPLICATION ACCREDITATIONS
   */
  getAccreditations: async (applicationId) => {
    const { data } = await request.get(`/applications/${applicationId}/accreditations`);
    return data;
  },

  addUpdateAccreditation: async (applicationId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/applications/${applicationId}/accreditations${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },

  deleteAccreditation: async (applicationId, accreditationId) => {
    const { data } = await request.delete(`/applications/${applicationId}/accreditations/${accreditationId}`);
    return data;
  },

  /*
   * APPLICATION STAFF MEMBERS
   */
  getStaffMembers: async (applicationId) => {
    const { data } = await request.get(`/applications/${applicationId}/staff`);
    return data;
  },

  addUpdateStaffMembers: async (applicationId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/applications/${applicationId}/staff${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },

  deleteStaffMember: async (applicationId, staffId) => {
    const { data } = await request.delete(`/applications/${applicationId}/staff/${staffId}`);
    return data;
  },

  addUpdateStaffMemberCert: async (applicationId, staffId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/applications/${applicationId}/staff${staffId ? `/${staffId}` : ''}/certifications${
        payload.id ? `/${payload.id}` : ''
      }`,
      payload,
    );
    return data;
  },

  deleteStaffMemberCert: async (applicationId, staffId, certId) => {
    const { data } = await request.delete(`/applications/${applicationId}/staff/${staffId}/certifications/${certId}`);
    return data;
  },

  /*
   * APPLICATION RESIDENTS
   */
  getResidents: async (applicationId) => {
    const { data } = await request.get(`/applications/${applicationId}/residents`);
    return data;
  },

  addUpdateResidents: async (applicationId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/applications/${applicationId}/residents${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },

  deleteResident: async (applicationId, residentId) => {
    const { data } = await request.delete(`/applications/${applicationId}/residents/${residentId}`);
    return data;
  },

  getChildResidents: async (applicationId) => {
    const { data } = await request.get(`/applications/${applicationId}/child-residents`);
    return data;
  },

  addUpdateChildResidents: async (applicationId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/applications/${applicationId}/child-residents${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },

  deleteChildResident: async (applicationId, residentId) => {
    const { data } = await request.delete(`/applications/${applicationId}/child-residents/${residentId}`);
    return data;
  },

  // Duplicate facility check
  checkIfFacilityIsADuplicate: async (careFacilityId, addressDto) => {
    const { data } = await request.post(
      `/care-facilities/addresses/dupe-check${careFacilityId !== undefined ? `?careFacilityId=${careFacilityId}` : ''}`,
      addressDto,
    );
    return data;
  },

  /*
   * Get organization for a user based on user ID
   */
  getOrganizationByUserId: async (userId) => {
    const { data } = await request.get(`/users/${userId}`);
    return data;
  },

  /*
   * BANKING PROFILES
   */
  getBankingProfiles: async (orgId) => {
    const { data } = await request.get(`/organizations/${orgId}/banking-profiles`);
    return data;
  },

  createBankingProfile: async (orgId, bankingDto) => {
    const { data } = await request.post(`/organizations/${orgId}/banking-profiles`, bankingDto);
    return data;
  },

  updateBankingProfile: async (orgId, profileId, bankingDto) => {
    const { data } = await request.put(`/organizations/${orgId}/banking-profiles/${profileId}`, bankingDto);
    return data;
  },

  deleteBankingProfile: async (orgId, profileId) => {
    const { data } = await request.delete(`/organizations/${orgId}/banking-profiles/${profileId}`);
    return data;
  },

  activateBankingProfile: async (careFacilityId, profileId) => {
    const { data } = await request.put(`/care-facilities/${careFacilityId}/banking-profiles/${profileId}/activate`);
    return data;
  },

  uploadVoidedCheck: async (orgId, profileId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await request.post(`/organizations/${orgId}/banking-profiles/${profileId}/voided-check`, formData);
    return data;
  },

  /*
   * TAX PROFILES
   */
  getTaxProfiles: async (orgId) => {
    const { data } = await request.get(`/organizations/${orgId}/tax-profiles`);
    return data;
  },

  createTaxProfile: async (orgId, dto) => {
    const { data } = await request.post(`/organizations/${orgId}/tax-profiles`, dto);
    return data;
  },

  updateTaxProfile: async (orgId, profileId, dto) => {
    const { data } = await request.put(`/organizations/${orgId}/tax-profiles/${profileId}`, dto);
    return data;
  },

  deleteTaxProfile: async (orgId, profileId) => {
    const { data } = await request.delete(`/organizations/${orgId}/tax-profiles/${profileId}`);
    return data;
  },

  activateTaxProfileOnFacility: async (facilityId, profileId) => {
    const { data } = await request.put(`/care-facilities/${facilityId}/tax-profiles/${profileId}/activate`);
    return data;
  },

  uploadW9: async (orgId, profileId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await request.post(`/organizations/${orgId}/tax-profiles/${profileId}/w9`, formData);
    return data;
  },
};
export { request };
export default api;
